import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useApiClient } from '../../service/API';
import PDFViewer from './PDFViewer';
import DocxViewer from './DocxViewer';
import ExcelViewer from './ExcelViewer';
import EmlViewer from './EmlViewer';
import MsgViewer from './MsgViewer';
import ImgViewer from './ImgViewer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { getApiErrorMessage } from "../../service/errorHandler";
import '../../styling/FileViewer.css';

const FileViewer = ({ folderName, fileInfo }) => {
  const apiClient = useApiClient();

  // state machine for fetch
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'error'
  const [error, setError] = useState(null);
  const [fileExtension, setFileExtension] = useState('');
  const [name, setFileName] = useState(fileInfo?.name || '');
  const [zoom, setZoom] = useState(1);

  // keep last good url so we never pass null to viewers
  const stableUrlRef = useRef(null);
  const inflightRef = useRef({ cancelled: false });
  const currentUrlRef = useRef(null); // for safe revocation

  const extensionFromName = (n) =>
    (n || '').includes('.') ? n.split('.').pop().toLowerCase() : '';

  const setStableUrl = (url) => {
    // revoke previous if different
    if (currentUrlRef.current && currentUrlRef.current !== url) {
      try { URL.revokeObjectURL(currentUrlRef.current); } catch {}
    }
    currentUrlRef.current = url;
    stableUrlRef.current = url;
  };

  const clearStableUrl = () => {
    if (currentUrlRef.current) {
      try { URL.revokeObjectURL(currentUrlRef.current); } catch {}
    }
    currentUrlRef.current = null;
    stableUrlRef.current = null;
  };

  const fetchFile = async () => {
    if (!fileInfo) return;
    inflightRef.current.cancelled = false;
    setStatus('loading');
    setError(null);

    try {
      const res = await apiClient.get('/files/retrieve', {
        params: { fileId: fileInfo.fileId },
        responseType: 'arraybuffer',
        headers: { Accept: 'application/json' },
      });

      if (inflightRef.current.cancelled) return;

      if (res.status === 200) {
        const blob = new Blob([res.data]);
        const url = URL.createObjectURL(blob);

        // ✅ URL & extension are updated together, in sync
        setStableUrl(url);
        setFileName(fileInfo.name);
        setFileExtension(extensionFromName(fileInfo.name));

        setStatus('ready');
      } else {
        throw new Error(`Server returned status: ${res.status}`);
      }
    } catch (err) {
      if (inflightRef.current.cancelled) return;
      setError(getApiErrorMessage(err, "Unable to fetch the file."));
      setStatus('error');
    }
  };

  // refetch when the selected file changes
  useEffect(() => {
    // cancel previous request & leave last-good URL intact
    inflightRef.current.cancelled = true;

    // start new request
    inflightRef.current = { cancelled: false };
    fetchFile();

    // cleanup on prop change/unmount
    return () => {
      inflightRef.current.cancelled = true;
      // do NOT clear stableUrlRef here; viewers will keep using last-good URL
      // we only revoke when we set a new URL or on final unmount below
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileInfo?.fileId, fileInfo?.name, folderName]);

  // revoke on final unmount
  useEffect(() => {
    return () => {
      clearStableUrl();
    };
  }, []);

  const safeUrl = stableUrlRef.current;
  const isImage = useMemo(() => ['jpg', 'jpeg', 'png'].includes(fileExtension), [fileExtension]);
  const isPdf = fileExtension === 'pdf';

  const handleZoomIn = (e) => { e.preventDefault(); setZoom((z) => Math.min(8, +(z + 0.1).toFixed(2))); };
  const handleZoomOut = (e) => { e.preventDefault(); setZoom((z) => Math.max(0.1, +(z - 0.1).toFixed(2))); };
  const handleZoomReset = (e) => { e.preventDefault(); setZoom(1); };

  // UI states
  if (error && !safeUrl) {
    return <div className="file-preview-text error">{error}</div>;
  }
  if (!safeUrl) {
    return (
      <div className="file-preview-text">
        <FontAwesomeIcon icon={faSpinner} className="fa-spin" /> Loading file...
      </div>
    );
  }

  return (
    <div className="file-preview-container">
      <div className='file-header'>
        <div className="file-name">{name || 'Untitled'}</div>

        <div>
          {(isPdf || isImage) && (
            <div>
              <button className="btn btn-primary ms-3" onClick={handleZoomOut}>Zoom Out (−)</button>
              <button className="btn btn-secondary ms-2" onClick={handleZoomReset}>Fit (100%)</button>
              <button className="btn btn-primary ms-2" onClick={handleZoomIn}>Zoom In (+)</button>
              <span className="ms-2 text-muted">{Math.round(zoom * 100)}%</span>
            </div>
          )}
        </div>

        <div className="file-download">
          <a href={safeUrl} download={name} className="download-button">
            Download File <FontAwesomeIcon icon={faDownload} />
          </a>
        </div>
      </div>

      <div className="file-preview">
        {isImage ? (
          <ImgViewer imgUrl={safeUrl} externalZoom={zoom} />
        ) : isPdf ? (
          // never pass a transient null/undefined here
          <PDFViewer fileUrl={safeUrl} zoom={zoom} />
        ) : fileExtension === 'docx' ? (
          <DocxViewer fileUrl={safeUrl} />
        ) : ['xls', 'xlsx'].includes(fileExtension) ? (
          <ExcelViewer fileUrl={safeUrl} />
        ) : fileExtension === 'eml' ? (
          <EmlViewer fileUrl={safeUrl} />
        ) : fileExtension === 'msg' ? (
          <MsgViewer fileUrl={safeUrl} />
        ) : (
          <div className="file-preview-text">Preview not available for this file type</div>
        )}
      </div>
    </div>
  );
};

export default FileViewer;
